import { ROUTES } from './routes'

/**
 * Where an old `/internal/analytics?tab=…` address goes now that the tab is its own page
 * under GTM:
 *   VRG-672 — `?tab=seo`        → `/internal/gtm/seo`      (SEO & AEO)
 *   VRG-678 — `?tab=affiliates` → `/internal/gtm/insiders`, and since VRG-684 straight to
 *             `/internal/gtm/affiliates` (Refer & earn + Vera Insiders) — one hop, not two
 *
 * Pure, so the guard (`test/gtm-seo-redirect.guard.spec.ts`) can exercise it without a
 * router. Returns `null` for any URL that is NOT a moved tab, which is the Analytics page's
 * normal case and must render it untouched.
 *
 * EVERY OTHER PARAM SURVIVES. The biweekly review email and its Slack post already sent
 * carry `seo=recommendations` and `seo=reviews&review=<id>`; `market=` and `range=` make a
 * bookmarked view (the Affiliates tab shared `range=` with Website). Only `tab` is dropped,
 * because neither destination has tabs and a stray `tab=` there would mean nothing. Order is
 * preserved as the visitor's URL had it.
 */
const MOVED_ANALYTICS_TABS: Readonly<Record<string, string>> = {
  seo: ROUTES.INTERNAL_GTM_SEO,
  affiliates: ROUTES.INTERNAL_GTM_AFFILIATES,
}

export const legacyAnalyticsTabTarget = (search: string): string | null => {
  const params = new URLSearchParams(search)
  const tab = params.get('tab')
  const target = tab ? MOVED_ANALYTICS_TABS[tab] : undefined
  if (!target) return null
  params.delete('tab')
  const rest = params.toString()
  return rest ? `${target}?${rest}` : target
}

/** VRG-672's name for the SEO half; kept so the existing guard and callers read unchanged. */
export const legacyAnalyticsSeoTarget = (search: string): string | null => {
  const tab = new URLSearchParams(search).get('tab')
  return tab === 'seo' ? legacyAnalyticsTabTarget(search) : null
}

/*
  VRG-684 — the GTM addresses that moved inside GTM itself. Same contract as above: pure (the
  guard `test/gtm-ia-redirect.guard.spec.ts` drives them without a router), every param the
  visitor's URL carried survives in its original order, and only a param the destination
  would misread is rewritten.
*/

/** Join a path and a param set, dropping a bare `?`. */
const withParams = (path: string, params: URLSearchParams): string => {
  const rest = params.toString()
  return rest ? `${path}?${rest}` : path
}

/** Sales' tab values (`GtmSalesPage`). Exported so the page and the redirects share one list. */
// VRG-691 — Leads and Companies sit beside the Pipeline they feed. VRG-701 — Deals, the list of
// every deal (a company can have several), sits right after the board it lists. VRG-713 (Spencer,
// 2026-10-16) — Companies before Leads, and the Outreach tab is called Campaigns (key `campaigns`;
// the old `?tab=outreach` redirects, `legacySalesTabTarget`). The channel stays Sales (#13) and the
// stored source channel stays `outreach`: only the tab's label and key moved. VRG-727 (Spencer,
// 2026-10-20) — the Leads tab is People (key `people`; `?tab=leads` redirects). People and
// Companies are the two things on the pipeline; "Lead" is the stage both start at.
export const SALES_TABS = ['pipeline', 'deals', 'companies', 'people', 'campaigns', 'playbook', 'templates'] as const
export type SalesTab = (typeof SALES_TABS)[number]

/** Sales tab keys that were renamed, old → new. */
const RENAMED_SALES_TABS: Readonly<Record<string, SalesTab>> = { outreach: 'campaigns', leads: 'people' }

/**
 * `/internal/gtm/sales?tab=outreach…` (VRG-684's key) → `?tab=campaigns…` (VRG-713), and
 * `?tab=leads…` → `?tab=people…` (VRG-727), every other param kept in its place. Returns `null` for
 * any other Sales URL, which is the page's normal case and must render untouched. A `<Route>`
 * cannot match on a query string, so the page applies it (and carries the router state across, so
 * a "show this person" hand-off from a lead journey survives the hop).
 */
export const legacySalesTabTarget = (search: string): string | null => {
  const params = new URLSearchParams(search)
  const renamed = RENAMED_SALES_TABS[params.get('tab') ?? '']
  if (!renamed) return null
  params.set('tab', renamed)
  return withParams(ROUTES.INTERNAL_GTM_SALES, params)
}

/*
  VRG-735 — GTM → Social is channel-first. The tab is the first PATH segment under
  `/internal/gtm/social` (LinkedIn · Instagram · Brand kit · Stats · Metrics), and the two channel
  tabs carry a sub-view in the next one (Calendar · Posts · Playbook), so a link opens exactly the
  view it names and the sidebar row stays lit on all of them (longest-match selection).
  `socialTarget` is the one place that turns any other Social address, including VRG-672's
  `?tab=` URLs, into a canonical one. Pure, so `test/gtm-social-redirect.guard.spec.ts` drives it.
*/
export const SOCIAL_TABS = ['linkedin', 'instagram', 'brand-kit', 'stats', 'metrics'] as const
export type SocialTab = (typeof SOCIAL_TABS)[number]
export const SOCIAL_CHANNEL_TABS = ['linkedin', 'instagram'] as const
export type SocialChannelTab = (typeof SOCIAL_CHANNEL_TABS)[number]
export const SOCIAL_VIEWS = ['calendar', 'posts', 'playbook'] as const
export type SocialView = (typeof SOCIAL_VIEWS)[number]

export const isSocialChannelTab = (tab: string): tab is SocialChannelTab => (SOCIAL_CHANNEL_TABS as readonly string[]).includes(tab)

/** The canonical address of a tab (and, on a channel tab, its sub-view; Calendar when omitted). */
export const socialPath = (tab: SocialTab, view?: SocialView): string =>
  isSocialChannelTab(tab) ? `${ROUTES.INTERNAL_GTM_SOCIAL}/${tab}/${view ?? 'calendar'}` : `${ROUTES.INTERNAL_GTM_SOCIAL}/${tab}`

/** Which tab and sub-view a pathname names; nulls for whatever it does not name. */
export const socialLocationOf = (pathname: string): { tab: SocialTab | null; view: SocialView | null } => {
  const rest = pathname.startsWith(ROUTES.INTERNAL_GTM_SOCIAL) ? pathname.slice(ROUTES.INTERNAL_GTM_SOCIAL.length) : ''
  const [tabSeg = '', viewSeg = ''] = rest.split('/').filter(Boolean)
  const tab = (SOCIAL_TABS as readonly string[]).includes(tabSeg) ? (tabSeg as SocialTab) : null
  const view = tab && isSocialChannelTab(tab) && (SOCIAL_VIEWS as readonly string[]).includes(viewSeg) ? (viewSeg as SocialView) : null
  return { tab, view }
}

/**
 * VRG-672's seven `?tab=` values → their new home. Overview folded into each channel's Playbook
 * (its cross-channel rules are that view's "Shared by both channels" section), and the old
 * cross-channel Calendar and Drafts open on LinkedIn, the primary channel.
 */
const LEGACY_SOCIAL_TABS: Readonly<Record<string, string>> = {
  overview: `${ROUTES.INTERNAL_GTM_SOCIAL}/linkedin/playbook`,
  linkedin: `${ROUTES.INTERNAL_GTM_SOCIAL}/linkedin/playbook`,
  instagram: `${ROUTES.INTERNAL_GTM_SOCIAL}/instagram/playbook`,
  calendar: `${ROUTES.INTERNAL_GTM_SOCIAL}/linkedin/calendar`,
  drafts: `${ROUTES.INTERNAL_GTM_SOCIAL}/linkedin/posts`,
  stats: `${ROUTES.INTERNAL_GTM_SOCIAL}/stats`,
  metrics: `${ROUTES.INTERNAL_GTM_SOCIAL}/metrics`,
}

/**
 * Where a Social address should be instead, or `null` when it is already canonical (the page's
 * normal case, rendered untouched). One hop; every param except a legacy `tab` is kept in order.
 *   `/internal/gtm/social?tab=<old>`   → that tab's new home (an unknown value → LinkedIn · Calendar)
 *   `/internal/gtm/social`             → LinkedIn · Calendar
 *   `/internal/gtm/social/linkedin`    → LinkedIn · Calendar (a channel tab needs a sub-view)
 *   `/internal/gtm/social/<unknown>`   → LinkedIn · Calendar
 *   `/internal/gtm/social/stats/extra` → `/internal/gtm/social/stats`
 */
export const socialTarget = (pathname: string, search: string): string | null => {
  const params = new URLSearchParams(search)
  const clean = pathname.replace(/\/+$/, '')
  const { tab, view } = socialLocationOf(clean)
  if (!tab) {
    const legacy = LEGACY_SOCIAL_TABS[params.get('tab') ?? '']
    params.delete('tab')
    return withParams(legacy ?? socialPath('linkedin'), params)
  }
  const canonical = socialPath(tab, view ?? undefined)
  return pathname === canonical ? null : withParams(canonical, params)
}

/**
 * `/internal/gtm/outreach?…` (VRG-679's page) → `/internal/gtm/sales?tab=…`. The old page had
 * its own tabs: `campaigns` (its default) becomes Sales' `campaigns` tab (VRG-713; it was Sales'
 * `outreach` from VRG-684); `playbook` and `templates` keep their names, because Sales has tabs of
 * the same name holding the same panels. Any other param is kept as it was. One hop.
 */
export const legacyOutreachTarget = (search: string): string => {
  const params = new URLSearchParams(search)
  const old = params.get('tab')
  const tab: SalesTab = old === 'playbook' || old === 'templates' ? old : 'campaigns'
  params.set('tab', tab)
  return withParams(ROUTES.INTERNAL_GTM_SALES, params)
}

/**
 * A campaign's old addresses → `/internal/gtm/sales/campaigns/:id?…`, params untouched, one hop:
 * `/internal/gtm/outreach/:id` (VRG-679) and `/internal/gtm/sales/outreach/:id` (VRG-684).
 */
export const legacyOutreachCampaignTarget = (id: string, search: string): string =>
  withParams(ROUTES.INTERNAL_GTM_SALES_CAMPAIGN(id), new URLSearchParams(search))

/**
 * A campaign's breadcrumb parent, `/internal/gtm/sales/campaigns`, and its VRG-684 address
 * `/internal/gtm/sales/outreach` → the Campaigns tab.
 */
export const salesCampaignsIndexTarget = (search: string): string => {
  const params = new URLSearchParams(search)
  params.set('tab', 'campaigns')
  return withParams(ROUTES.INTERNAL_GTM_SALES, params)
}

/** Affiliates' tab values (`GtmAffiliatesPage`). */
export const AFFILIATES_TABS = ['refer', 'insiders'] as const
export type AffiliatesTab = (typeof AFFILIATES_TABS)[number]

/** `/internal/gtm/insiders?…` (VRG-678) → `/internal/gtm/affiliates?…`; `range=` and the rest kept. */
export const legacyInsidersTarget = (search: string): string =>
  withParams(ROUTES.INTERNAL_GTM_AFFILIATES, new URLSearchParams(search))

/**
 * `/internal/people?tab=referrals…` (VRG-658's tab, on People and on the classic Signups page) →
 * `/internal/gtm/affiliates?tab=refer…`. Returns `null` for every other People/Signups URL, which
 * is their normal case and must render untouched. `tab` is REWRITTEN rather than dropped:
 * Affiliates has tabs too, and the referrals table lives on Refer & earn.
 */
export const legacyPeopleReferralsTarget = (search: string): string | null => {
  const params = new URLSearchParams(search)
  if (params.get('tab') !== 'referrals') return null
  params.set('tab', 'refer')
  return withParams(ROUTES.INTERNAL_GTM_AFFILIATES, params)
}

/**
 * VRG-727 — `/internal/people?…` (the Access page's address from 2026-10-01 until the rename) and
 * `/internal/signups?…` → `/internal/access?…`, every param kept, in ONE hop: a `?tab=referrals`
 * goes straight to Refer & earn (`legacyPeopleReferralsTarget`) rather than via Access. Callers
 * pass the router state through, so a linked-search hand-off (a lead journey's "Access" link)
 * still opens on its person.
 */
export const legacyAccessTarget = (search: string): string =>
  legacyPeopleReferralsTarget(search) ?? withParams(ROUTES.INTERNAL_ACCESS, new URLSearchParams(search))

/*
  VRG-684 (scope addition, 2026-10-08) — the Analytics section: Website and Product.
*/

/**
 * `/internal` and `/internal/analytics` (both rendered the Website page) → where they go now, in
 * ONE hop: a moved GTM tab (`?tab=seo|affiliates`) straight to its GTM page, otherwise
 * `/internal/analytics/website` with every param (`range=` and the rest) kept.
 */
export const analyticsIndexTarget = (search: string): string =>
  legacyAnalyticsTabTarget(search) ?? withParams(ROUTES.INTERNAL_ANALYTICS_WEBSITE, new URLSearchParams(search))

/** `/internal/usage?…` (Tours + Ask) → `/internal/analytics/product?…`, `tab=` and all kept. */
export const legacyUsageTarget = (search: string): string =>
  withParams(ROUTES.INTERNAL_ANALYTICS_PRODUCT, new URLSearchParams(search))

/*
  VRG-751 (Spencer, 2026-10-23) — GTM → Insights is ONE page with four PATH tabs: Articles ·
  Ideas · Interviews · Analytics. The tab is the path, so a link opens the tab it names and the
  sidebar row stays lit across all four.

  Three generations of address reach it, and each redirects in ONE hop with every param kept:
    VRG-674  /internal/gtm/articles?tab=…       (the Articles page, which had its OWN tab row)
    VRG-734  /internal/gtm/content[/<tab>]      ("Content marketing", three tabs)
    VRG-751  /internal/gtm/insights/<tab>       here
  Pure, so `test/gtm-ia-redirect.guard.spec.ts` drives them without a router.
*/
/*
  The ROW ORDER, and it is also the landing (Spencer, 2026-10-29): Analytics · Articles ·
  Interviews · Ideas — results first, then the work that produced them, with Ideas last because an
  idea is the least-committed thing here.

  THE BARE `/internal/gtm/insights` LANDS ON ANALYTICS, not on the first tab by accident: the
  sidebar's Insights row links to the bare path, so the landing is what a staffer actually sees,
  and a row whose first tab is never the one you arrive on reads as a bug. The LEGACY addresses
  below are unchanged — `/internal/gtm/articles` and `/internal/gtm/content` NAMED the articles
  page, so they still land there; only the index moved.
*/
// 'rules' is VRG-741's read-only editorial-rules snapshot, kept reachable when VRG-751
// replaced this page's tab model (it had been ARTICLES_TABS' fourth tab). LAST in the row
// by the same logic the block above states: it is reference material, not work in progress.
export const INSIGHTS_TABS = ['analytics', 'articles', 'interviews', 'ideas', 'rules'] as const
export type InsightsTab = (typeof INSIGHTS_TABS)[number]

export const INSIGHTS_TAB_PATHS: Readonly<Record<InsightsTab, string>> = {
  analytics: ROUTES.INTERNAL_GTM_INSIGHTS_ANALYTICS,
  articles: ROUTES.INTERNAL_GTM_ARTICLES,
  interviews: ROUTES.INTERNAL_GTM_INSIGHTS_INTERVIEWS,
  ideas: ROUTES.INTERNAL_GTM_INSIGHTS_IDEAS,
  rules: ROUTES.INTERNAL_GTM_INSIGHTS_RULES,
}

/**
 * The tab a pathname names; anything else under the parent (its bare index) is Analytics. No tab
 * path is a prefix of another, so the find is order-independent — the array's order is the row's.
 */
export const insightsTabOf = (pathname: string): InsightsTab =>
  INSIGHTS_TABS.find((t) => pathname === INSIGHTS_TAB_PATHS[t] || pathname.startsWith(`${INSIGHTS_TAB_PATHS[t]}/`)) ??
  'analytics'

/**
 * VRG-674's `/internal/gtm/articles?tab=…` → the tab's own path. `tab` is DROPPED rather than
 * carried, because the tab is the path now and a stray `tab=` would mean nothing; every other
 * param (`q=`, a filter, a saved view) survives in its original order.
 *
 * `?tab=guests` was VRG-740's second guest list. VRG-752 retired it, so the address lands on the
 * one that remains — Interviews' own Guests view — rather than on a tab that no longer exists.
 */
export const legacyArticlesTarget = (search: string): string => {
  const params = new URLSearchParams(search)
  const tab = params.get('tab')
  params.delete('tab')
  if (tab === 'ideas') return withParams(INSIGHTS_TAB_PATHS.ideas, params)
  if (tab === 'rules') return withParams(INSIGHTS_TAB_PATHS.rules, params)
  if (tab === 'guests') {
    params.set('view', 'guests')
    return withParams(INSIGHTS_TAB_PATHS.interviews, params)
  }
  return withParams(INSIGHTS_TAB_PATHS.articles, params)
}

/** VRG-734's `/internal/gtm/content/<tab>` → VRG-751's `/internal/gtm/insights/<tab>`. */
const MOVED_CONTENT_PATHS: Readonly<Record<string, string>> = {
  [ROUTES.INTERNAL_GTM_CONTENT_LEGACY]: INSIGHTS_TAB_PATHS.articles,
  [ROUTES.INTERNAL_GTM_CONTENT_ARTICLES_LEGACY]: INSIGHTS_TAB_PATHS.articles,
  [ROUTES.INTERNAL_GTM_CONTENT_VERAFIED_LEGACY]: INSIGHTS_TAB_PATHS.interviews,
  [ROUTES.INTERNAL_GTM_CONTENT_ANALYTICS_LEGACY]: INSIGHTS_TAB_PATHS.analytics,
}

/**
 * Where a VRG-734 Content-marketing address goes now, params untouched and in order (the VERAfied
 * tab reads `view=`, Articles reads `q=` and its filters). `null` for anything else, which is not
 * a moved address and must not be redirected.
 */
export const legacyContentTarget = (pathname: string, search: string): string | null => {
  const target = MOVED_CONTENT_PATHS[pathname.replace(/\/$/, '')]
  return target ? withParams(target, new URLSearchParams(search)) : null
}

/**
 * VRG-733 — an article's page tabs (`ArticleDetailPage`, `?tab=`). Overview is the default.
 * Audio and Analytics come later and get no empty tab meanwhile (see the page's docblock).
 */
export const ARTICLE_TABS = ['overview', 'cover'] as const
export type ArticleTab = (typeof ARTICLE_TABS)[number]

/**
 * VRG-733's own earlier article addresses, both on this branch before the VRG-734 merge moved the
 * page under Insights → Articles: `/internal/gtm/articles/:section/:slug[?…]` and its first,
 * standalone Cover page `…/:slug/cover`. One hop to the new path, every param kept; the Cover page
 * lands on `?tab=cover`.
 */
export const legacyArticleTarget = (section: string, slug: string, search: string, cover = false): string => {
  const params = new URLSearchParams(search)
  if (cover) params.set('tab', 'cover')
  return withParams(ROUTES.INTERNAL_GTM_ARTICLE(encodeURIComponent(section), encodeURIComponent(slug)), params)
}
